import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
// const history = useHistory();
import * as firestore from "firebase/firestore";
import { db, auth } from "../../scripts/app";
import * as gridClasses from "../../css/grid.module.css";

import {
  DataGridPro,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { DateTime, Duration } from "luxon";
import { DateRangePicker } from "@mui/x-date-pickers-pro";

import { today } from "../../common/consts/DateRangePresets";
import {
  Box,
  Button,
  CircularProgress,
  styled,
  Tooltip,
  tooltipClasses,
} from "@mui/material";
import Breadcrumbs from "../../common/Breadcrumbs/Breadcrumbs";
import { presets } from "../../common/consts/DateRangePresets";
import { v4 } from "uuid";
let render = 0;

const CELL_METADATA_FIELD = "__cellMetadata";
const COMMENT_PREVIEW_LENGTH = 400;
const PHOTO_STACK_OFFSET = 12;
const PHOTO_STACK_MAX_OFFSET = 48;
const LOADING_GRID_COLUMNS = Array.from({ length: 9 }, (_, index) => ({
  field: `loading-column-${index}`,
  headerName: "",
  minWidth: 175,
  flex: 1,
  sortable: false,
}));

const ResultTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#ffffff",
    color: "#3b3630",
    overflow: "hidden",
    border: "1px solid #d9dee5",
    borderRadius: "1.5rem",
    padding: "0.5rem",
    boxShadow: "3px 3px 6px #00000050",
    boxSizing: "border-box",
    width: "13rem",
    maxWidth: "13rem",
  },
}));

function OverflowTooltip({ value, tabIndex = -1 }) {
  const ref = React.useRef(null);
  const [overflowing, setOverflowing] = useState(false);
  const text = String(value ?? "");

  React.useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;

    const checkOverflow = () => {
      setOverflowing(element.scrollWidth > element.clientWidth);
    };

    checkOverflow();

    const observer = new ResizeObserver(checkOverflow);
    observer.observe(element);

    return () => observer.disconnect();
  }, [text]);

  return (
    <ResultTooltip
      describeChild
      placement="right-start"
      title={
        overflowing ? (
          <div className={gridClasses.resultTooltipContent}>
            <div className={gridClasses.mediaTooltipComment}>{text}</div>
          </div>
        ) : (
          ""
        )
      }
    >
      <div
        ref={ref}
        tabIndex={tabIndex}
        style={{
          width: "100%",
          minWidth: 0,
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
        }}
      >
        {text}
      </div>
    </ResultTooltip>
  );
}

function getAnswerComment(answer) {
  if (!answer || typeof answer !== "object") return "";

  const comment = [answer.comment, answer.caption].find(
    (value) => typeof value === "string" && value.trim().length > 0
  );

  return comment?.trim() ?? "";
}

function getEventComment(event) {
  const answerComment = getAnswerComment(event?.answer);
  if (answerComment) return answerComment;

  const componentComment = event?.component?.comment;
  return typeof componentComment === "string" ? componentComment.trim() : "";
}

function getCellMetadata(row, field) {
  return row?.[CELL_METADATA_FIELD]?.[field] ?? {};
}

function getCellComment(row, field) {
  const comment = getCellMetadata(row, field).comment;
  return typeof comment === "string" ? comment.trim() : "";
}

function getCommentPreview(comment) {
  const normalisedComment = typeof comment === "string" ? comment : "";

  return {
    text: normalisedComment.slice(0, COMMENT_PREVIEW_LENGTH),
    isTruncated: normalisedComment.length > COMMENT_PREVIEW_LENGTH,
  };
}

function getTaskDetailEventHref(workflowId, taskId, eventId) {
  const taskDetailHref = `/main/launchpad/task-detail/${encodeURIComponent(
    workflowId
  )}/${encodeURIComponent(taskId)}`;

  return eventId === undefined || eventId === null || eventId === ""
    ? taskDetailHref
    : `${taskDetailHref}?eventId=${encodeURIComponent(eventId)}`;
}

function getMediaSource(value) {
  if (typeof value === "string") return value;
  return value?.url ?? value?.downloadURL ?? value?.downloadUrl ?? "";
}

function getEventPhotos(event) {
  const photos = [];
  const componentType = event?.component?.type;

  const addPhotos = (values, objectFit = "cover") => {
    const candidates = Array.isArray(values) ? values : [values];

    candidates.forEach((candidate) => {
      const url = getMediaSource(candidate);
      if (!url || photos.some((photo) => photo.url === url)) return;
      photos.push({ url, objectFit });
    });
  };

  if (componentType === "photo") {
    addPhotos(event.answer?.photo);
  } else if (componentType === "signature") {
    addPhotos(event.answer?.signature, "contain");
  }

  addPhotos(event.answer?.photos);
  addPhotos(event.images);

  return photos;
}

function getCellPhotos(row, field) {
  const photos = getCellMetadata(row, field).photos;
  return Array.isArray(photos) ? photos : [];
}

function cellHasSupplementalContent(row, field) {
  const metadata = getCellMetadata(row, field);
  const photos = getCellPhotos(row, field);
  const isMediaCell = metadata.type === "photo" || metadata.type === "signature";
  const hasAdditionalPhotos = isMediaCell
    ? photos.length > 1
    : photos.length > 0;

  return Boolean(getCellComment(row, field) || hasAdditionalPhotos);
}

function PhotoStack({ photos, label }) {
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);

  useEffect(() => {
    setActivePhotoIndex(0);
  }, [photos.length]);

  const orderedPhotos = photos.map((_, offset) => {
    const photoIndex = (activePhotoIndex + offset) % photos.length;
    return photos[photoIndex];
  });
  const canCycle = photos.length > 1;
  const photoOffset = canCycle
    ? Math.min(
        PHOTO_STACK_OFFSET,
        PHOTO_STACK_MAX_OFFSET / (photos.length - 1)
      )
    : 0;
  const stackOffset = Math.max(photos.length - 1, 0) * photoOffset;

  const cyclePhotos = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canCycle) return;
    setActivePhotoIndex((currentIndex) => (currentIndex + 1) % photos.length);
  };

  const keepTooltipFocus = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const stopGridMouseUp = (event) => {
    event.stopPropagation();
  };

  return (
    <div className={gridClasses.photoStackArea}>
      <button
        type="button"
        className={gridClasses.photoStack}
        // style={{
        //   width: `calc(10rem + ${stackOffset}px)`,
        //   height: `calc(10rem + ${stackOffset}px)`,
        // }}
        style={{
          width: `calc(8rem + ${stackOffset}px)`,
          height: `calc(8rem + ${stackOffset}px)`,
        }}
        onMouseDown={keepTooltipFocus}
        onMouseUp={stopGridMouseUp}
        onClick={cyclePhotos}
        disabled={!canCycle}
        aria-label={
          canCycle
            ? `Showing ${label} ${activePhotoIndex + 1} of ${photos.length}. Show next image.`
            : `Showing ${label}`
        }
      >
        {orderedPhotos.map((photo, stackIndex) => (
          <img
            key={photo.url}
            className={gridClasses.photoStackImage}
            src={photo.url}
            alt=""
            draggable="false"
            style={{
              top: stackIndex * photoOffset * 0.5,
              left: stackIndex * photoOffset,
              zIndex: photos.length - stackIndex,
              objectFit: photo.objectFit,
            }}
          />
        ))}
      </button>
    </div>
  );
}

function ResultTooltipContent({
  photos,
  comment,
  taskDetailHref,
  mediaLabel = "photo",
}) {
  const stackLabel = photos.length > 1 ? "image" : mediaLabel;
  const commentPreview = getCommentPreview(comment);

  const stopGridEvent = (event) => {
    event.stopPropagation();
  };

  return (
    <div className={gridClasses.resultTooltipContent}>
      {photos.length > 0 && <PhotoStack photos={photos} label={stackLabel} />}
      {comment && (
        <div
          className={`${gridClasses.mediaTooltipComment} ${
            photos.length > 0 ? gridClasses.mediaTooltipCommentWithDivider : ""
          }`}
        >
          {commentPreview.text}
          {commentPreview.isTruncated && (
            <>
              {"... "}
              <a
                className={gridClasses.tooltipReadMoreLink}
                href={taskDetailHref}
                onMouseDown={stopGridEvent}
                onMouseUp={stopGridEvent}
                onClick={stopGridEvent}
              >
                read more
              </a>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function TaskResults() {
  const { tsstartDate, tsendDate, workflowID, subjectID, originFrom } =
    useParams();
  const [negativeItems, setNegativeItems] = useState([
    { value: "no", colour: `${gridClasses.chip} ${gridClasses.negativeChip}` },
    { value: "yes", colour: `${gridClasses.chip} ${gridClasses.positiveChip}` },
    { value: "-", colour: "#0000ff" },
  ]);
  let subjectColumns = [];
  let initialRange = [
    tsstartDate ? DateTime.fromMillis(Number(tsstartDate)) : today[0],
    tsendDate ? DateTime.fromMillis(Number(tsendDate)) : today[1],
  ];
  let headerColour = "#FF0000";
  let buttonColour = "#00FF00";
  let originName = "Launchpad";
  let originLink = "/main/launchpad";
  const [dateRange, setDateRange] = useState(initialRange);
  const [workflow, setWorkflow] = useState();
  const [columns, setColumns] = useState([]);
  console.log("columns", columns);
  const [columnGroupingModel, setColumnGroupingModel] = useState([]);
  console.log("columnGroupingModel", columnGroupingModel);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [queryError, setQueryError] = useState(false);
  const apiRef = useGridApiRef();

  switch (originFrom) {
    case "category-stats":
      headerColour = "#d8d2e7";
      buttonColour = "#562ea6";
      originName = "Category Stats";
      break;
    case "user-stats":
      headerColour = "#d4cfd6";
      buttonColour = "#9638a6";
      originName = "User Stats";
      break;
    case "workflow-history":
      headerColour = "#d4e4d4";
      buttonColour = "#4caf50";
      originName = "Workflow Task";
      break;
    case "workflow-stats":
      headerColour = "#bed3d3";
      buttonColour = "#4f7b7b";
      originName = "Workflow Stats";
      break;
  }
  const containsEnd = (data, valueToCompare, fieldToCheck) => {
    let doesContain = false;
    let indexOfValue = 0;
    data.forEach((item, index) => {
      if (item[fieldToCheck].type === valueToCompare) {
        doesContain = true;
        indexOfValue = index;
      }
    });
    return doesContain;
  };
  const contains = (data, valueToCompare, fieldToCheck) => {
    let doesContain = false;
    let indexOfValue = 0;
    data.forEach((item, index) => {
      if (item[fieldToCheck] === valueToCompare) {
        doesContain = true;
        indexOfValue = index;
      }
    });
    return [doesContain, indexOfValue];
  };

  const createResultColumn = (column) => ({
    field: column.field,
    headerName: column.headerText,
    minWidth: 175,
    flex: 1,
    renderCell: renderResultCell,
    cellClassName: getResultCellClassName,
  });

  const addColumns = (doc) => {
    console.log(
      "tagged data",
      doc.data(),
      "securityTags",
      doc.data().securityTags
    );
	const newColumns = [];
	const newGroups = [];
    setColumns([]);
    setWorkflow(doc.data().name);
    doc.data().workflowColumns.forEach((column) => {
      console.log("WorkflowColumns", column);
      if ("columns" in column) {
        let children = [];
        column.columns.forEach((subColumn) => {
          console.log("subcolumn id", subColumn.field);
		  newColumns.push(createResultColumn(subColumn));
		  if(!contains(children, subColumn.field, 'field')[1]){
          		children.push({ field: subColumn.field });
			}
        });
        let id = v4();
        newGroups.push({
                groupId: id,
                headerName: column.headerText,
                children: children,
              });
      } else if (column.field === "detailsUrl") {
        newColumns.push({
            field: column.field,
            headerName: column.headerText,
            // flex:1,
            minWidth: 175,
            renderCell: (params) => {
              console.log('viewdetail params', params.value.split("="))
              const splitHref = params.value.split("=")
              const taskId = splitHref[2]
              const workflowId = splitHref[1].split("&")[0]
              return (
                <Button
                  variant="contained"
                  href={`/main/launchpad/task-detail/${workflowId}/${taskId}`}
                  sx={{ backgroundColor: buttonColour, borderRadius: 20 }}
                >
                  View Detail
                </Button>
              );
            },
          });
      } else {
        console.log("chipcolumn id", column.field);
        newColumns.push(createResultColumn(column));
      }
    });
	setColumns(newColumns);
	setColumnGroupingModel(newGroups);
  };
  useEffect(() => {
    let ignore = false;
    async function getWorkflow() {
      console.log("getWorkflows called");
      const docRef = firestore.collection(db, "workflows");
      console.log("docRef", docRef);
      const untaggedQuery = firestore.query(
        firestore.collection(db, "workflows"),
        firestore.where("hasSecurityTags", "==", false),
        firestore.where("id", "==", workflowID)
      );
      const untaggedSnap = await firestore.getDocs(untaggedQuery);
      untaggedSnap.forEach(addColumns);
      const token = await auth.currentUser?.getIdTokenResult();
      console.log("token", token);
      const securityTags = token?.claims.securityTags;
      console.log("securityTags", securityTags);
      if (securityTags?.length > 0 && !ignore) {
        const taggedQuery = firestore.query(
          firestore.collection(db, "workflows"),
          firestore.where("securityTags", "array-contains-any", securityTags),
          firestore.where("id", "==", workflowID)
        );
        const taggedSnap = await firestore.getDocs(taggedQuery);
        taggedSnap.forEach(addColumns);
      }
    }
    console.log("should not ignore");
    getWorkflow()
      .then(() => dateQuery())
      .catch((e) => {
        console.error("Error Getting Workflow: ", e);
        setQueryError(true);
        setLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, []);

  async function dateQuery() {
    console.log(
      "dateQuery called: startDate",
      tsstartDate,
      "endDate",
      tsendDate
    );
    setLoading(true);
    setQueryError(false);
    setRows([]);

    try {
      let queryWorkflowTasks = firestore.query(
        firestore.collection(db, "workflows", workflowID, "tasks"),
        firestore.where(
          "startedAt",
          ">=",
          firestore.Timestamp.fromDate(dateRange[0].toJSDate())
        ),
        firestore.where(
          "startedAt",
          "<=",
          firestore.Timestamp.fromDate(dateRange[1].toJSDate())
        ),
        firestore.orderBy("startedAt", "desc")
      );

      console.log("subjectID", subjectID);

      if (subjectID) {
        console.log("subject id found in date range query");
        queryWorkflowTasks = firestore.query(
          firestore.collection(db, "workflows", workflowID, "tasks"),
          firestore.where(
            "startedAt",
            ">=",
            firestore.Timestamp.fromDate(dateRange[0].toJSDate())
          ),
          firestore.where(
            "startedAt",
            "<=",
            firestore.Timestamp.fromDate(dateRange[1].toJSDate())
          ),
          firestore.where("primarySubject.id", "==", subjectID),
          firestore.orderBy("startedAt", "desc")
        );
      }

      const queryWorkflowTasksSnapshot = await firestore.getDocs(
        queryWorkflowTasks
      );
      const queriedRows = [];

      queryWorkflowTasksSnapshot.forEach((doc) => {
        if (doc.exists) {
          console.log("task in date range", doc.data());
          let task = doc.data();
          let newRow = {
            startedAt: DateTime.fromSeconds(task.startedAt.seconds),
            focus: task.primarySubject?.name,
            taskDuration: 0,
            startUser: task.events[0].user,
            taskStatus: containsEnd(task.events, "end", "component")
              ? "Completed"
              : "In Progress",
            lastAccessed: DateTime.fromSeconds(
              task.events[task.events.length - 1].processedAt.seconds
            ).toLocaleString(DateTime.DATETIME_SHORT),
            id: task.id,
            detailsUrl: `/task-detail.html?workflowid=${workflowID}&taskid=${task.id}`,
          };

          newRow = { ...newRow, ...processRTKTask(doc.data()) };
          queriedRows.push(newRow);
        }
      });
      setRows(queriedRows);
    } catch (error) {
      console.error("Error getting task instance documents: ", error);
      setQueryError(true);
    } finally {
      setLoading(false);
    }
  }

  let currentStartTime = 0;

  function getComponentInfo(event) {
    let output = `====== Event Type: ${event.component.type.toUpperCase()} =======
    Event ID: ${event.id}
    Event processed using: ${
      event.device !== undefined ? event.device.channel : "Device type unknown"
    }
    Event carried out by: ${event.user.name}
    Event carried out by: ${event.user.email}
    Location lat: ${
      event.location !== undefined
        ? event.location.latitude
        : "Latitude unknown"
    }
    Location long: ${
      event.location !== undefined
        ? event.location.longitude
        : "Longitude unknown"
    }
    Processed at ${new Date(event.processedAt.seconds * 1000)}
    `;

    let cellData = {
      type: event.component.type,
      id: event.id ?? event.component?.id,
      comment: getEventComment(event),
      photos: getEventPhotos(event),
      channel: `${
        event.device !== undefined
          ? event.device.channel
          : "Device type unknown"
      }`,
      userName: event.user.name,
      userEmail: event.user.email,
      latitude: `${
        event.location !== undefined
          ? event.location.latitude
          : "Latitude unknown"
      }`,
      longitude: `${
        event.location !== undefined
          ? event.location.longitude
          : "Longitude unknown"
      }`,
      time: new Date(event.processedAt.seconds * 1000),
    };

    switch (event.component.type) {
      case "start":
        cellData.axisLabel = "Task Started";
        cellData.value = DateTime.fromSeconds(
          event.processedAt.seconds
        ).toLocaleString(DateTime.DATETIME_SHORT);
        cellData.type = "start";
        break;
      case "decision":
        cellData.type = "decision";
        break;
      case "scan":
        cellData.value = event.answer.subject.name;
        cellData.axisLabel = event.component.rootId;
        cellData.rootId = event.component.rootId;
        cellData.type = "scan";
        break;
      case "subjectPicker":
        cellData.value = event.answer.subject.name;
        cellData.axisLabel = event.component.rootId;
        subjectColumns.push(event.component.rootId);
        cellData.rootId = event.component.rootId;
        break;
      case "hub":
        cellData.type = "hub";
        break;
      case "variable":
        cellData.value = event.stringValue ?? event.numberValue;
        cellData.axisLabel = event.component.rootId;
        cellData.rootId = event.component.rootId;
        cellData.type = "variable";
        break;
      case "calculation":
        output =
          output +
          `Event leftOperand ${event.component.leftOperand}, Operator ${
            event.component.operator
          }, Axis Label ${
            event.component.axisLabel ? event.component.axisLabel : "noLabel"
          }`;
        cellData.type = "calculation";
        break;
      case "subtask":
        output =
          output +
          `Event Subtask: ${event.component.name}
            Axis Label ${
              event.component.axisLabel ? event.component.axisLabel : "noLabel"
            }`;
        cellData.type = "subtask";
        break;
      case "singleSelect":
      case "SingleSelect":
        output =
          output +
          `Single Select: ${event.component.question}
            Single Select: ${event.answer.text}
            Axis Label ${
              event.component.axisLabel ? event.component.axisLabel : "noLabel"
            }`;
        cellData.value = event.answer.text;
        if ("axisLabel" in event.component)
          cellData.axisLabel = event.component.rootId;
        if ("axisLabel" in event.component)
          cellData.rootId = event.component.rootId;
        cellData.type = "singleSelect";
        break;
      case "text":
      case "TextComponent":
        output =
          output +
          `Text: ${event.component.question}
            Text: ${event.answer.text}
            Axis Label: ${
              event.component.axisLabel ? event.component.axisLabel : "noLabel"
            }`;
        event.component.question
          ? (cellData.question = event.component.question)
          : console.log(" question ");
        if ("axisLabel" in event.component)
          cellData.axisLabel = event.component.rootId;
        if ("axisLabel" in event.component)
          cellData.rootId = event.component.rootId;
        cellData.value = event.answer.text;
        cellData.type = "text";
        break;
      case "yesNo":
        output =
          output +
          `Axis Label ${
            event.component.axisLabel ? event.component.axisLabel : "noLabel"
          }`;
        event.component.question &&
          (cellData.question = event.component.question);
        if ("axisLabel" in event.component)
          cellData.axisLabel = event.component.rootId;
        if ("axisLabel" in event.component)
          cellData.rootId = event.component.rootId;
        cellData.value = event.answer.text;
        cellData.type = "yesNo";
        break;
      case "signature":
        cellData.type = "signature";
        if ("axisLabel" in event.component) {
          cellData.axisLabel = event.component.rootId;
          cellData.rootId = event.component.rootId;
        }
        cellData.value = event.answer.signature;

        break;
      case "issue":
        output =
          output +
          `Axis Label ${
            event.component.axisLabel ? event.component.axisLabel : "noLabel"
          }
                Name: ${event.component.issueType.name}
            Deadline: ${event.issue.deadline}`;
        if ("axisLabel" in event.component)
          cellData.axisLabel = event.component.rootId;
        if ("axisLabel" in event.component)
          cellData.rootId = event.component.rootId;
        cellData.value = event.issue.summary;
        cellData.type = "issue";

        break;
      case "browserMessage":
        cellData.type = "browserMessage";

        break;
      case "review":
        cellData.type = "review";

        break;
      case "photo":
        if ("axisLabel" in event.component)
          cellData.axisLabel = event.component.rootId;
        if ("axisLabel" in event.component)
          cellData.rootId = event.component.rootId;
        cellData.type = "photo";
        cellData.value = event.answer.photo;
        break;
      case "email":
        cellData.type = "email";

        break;
      case "dateTime":
        if (event.answer.timeAt) {
          cellData.value = DateTime.fromSeconds(event.answer.timeAt.seconds).toLocaleString(DateTime.TIME_SIMPLE);
        } else if (event.answer.duration) {
          cellData.value = event.answer.duration;
        } else if (event.answer.dateAt) {
          cellData.value = DateTime.fromSeconds(event.answer.dateAt.seconds).toLocaleString(DateTime.DATE_SHORT);
        } else if (event.answer.dateTimeAt) {
          cellData.value = DateTime.fromSeconds(event.answer.dateTimeAt.seconds).toLocaleString(DateTime.DATETIME_SHORT);
        } else if (event.answer.timeFrom && event.answer.timeTo) {
          let from = DateTime.fromSeconds(event.answer.timeFrom.seconds).toLocaleString(DateTime.TIME_SIMPLE);
          let to = DateTime.fromSeconds(event.answer.timeTo.seconds).toLocaleString(DateTime.TIME_SIMPLE);
          cellData.value = `${from} - ${to}`;
        } else if (event.answer.dateFrom && event.answer.dateTo) {
          let from = DateTime.fromSeconds(event.answer.dateFrom.seconds).toLocaleString(DateTime.DATE_SHORT);
          let to = DateTime.fromSeconds(event.answer.dateTo.seconds).toLocaleString(DateTime.DATE_SHORT);
          cellData.value = `${from} - ${to}`;
        } else if (event.answer.dateTimeFrom && event.answer.dateTimeTo) {
          let from = DateTime.fromSeconds(event.answer.dateTimeFrom.seconds).toLocaleString(DateTime.DATETIME_SHORT);
          let to = DateTime.fromSeconds(event.answer.dateTimeTo.seconds).toLocaleString(DateTime.DATETIME_SHORT);
          cellData.value = `${from} - ${to}`;
        }

        if ("axisLabel" in event.component)
          cellData.axisLabel = event.component.rootId;
        if ("axisLabel" in event.component)
          cellData.rootId = event.component.rootId;
        cellData.type = "dateTime";

        break;
      case "multiSelect":
        cellData.type = "multiSelect";

        let multiSelectItemsString = "";

        event.answer.options.forEach((item, index) => {
          console.log("index", index);
          if (index == 0) {
            multiSelectItemsString = multiSelectItemsString + item.text;
          } else if (index !== event.answer.options.length) {
            multiSelectItemsString = multiSelectItemsString + ", " + item.text;
          } else {
            multiSelectItemsString = multiSelectItemsString + item.text;
          }
        });

        cellData.value = multiSelectItemsString;
        cellData.type = "multiSelect";
        if ("axisLabel" in event.component)
          cellData.axisLabel = event.component.rootId;
        if ("axisLabel" in event.component)
          cellData.rootId = event.component.rootId;
        break;
      case "numeric":
        cellData.type = "numeric";
        cellData.value = event.answer.number;
        if ("axisLabel" in event.component)
          cellData.axisLabel = event.component.rootId;
        if ("axisLabel" in event.component)
          cellData.rootId = event.component.rootId;
        break;
      case "location":
        if (event.answer.location != undefined) {
          if (event.answer.location.latitude.toFixed(6) != "0.000000") {
            cellData.value =
              event.answer.location.latitude.toFixed(6) +
              " , " +
              event.answer.location.longitude.toFixed(6);
          } else {
            cellData.value = "Not available";
          }
        } else {
          cellData.value = "Not available";
        }

        cellData.type = "location";
        if ("axisLabel" in event.component) cellData.axisLabel = "geopoint";
        break;
      case "reference":
        cellData.type = "reference";
        cellData.value = event.reference;
        if ("axisLabel" in event.component)
          cellData.axisLabel = event.component.rootId;
        if ("axisLabel" in event.component)
          cellData.rootId = event.component.rootId;
        break;
      case "End":
      case "end":
        cellData.value = DateTime.fromSeconds(
          event.processedAt.seconds
        ).toLocaleString(DateTime.DATETIME_SHORT);
        cellData.axisLabel = "Task Ended";

        cellData.type = "end";
        break;
      default:
    }

    if (cellData.axisLabel) {
      return cellData;
    }
  }

  function getResultCellClassName(params) {
    return [
      gridClasses.resultGridCell,
      cellHasSupplementalContent(params.row, params.field)
        ? gridClasses.commentedGridCell
        : "",
    ]
      .filter(Boolean)
      .join(" ");
  }

  function renderMediaResultCell(
    params,
    metadata,
    comment,
    photos,
    taskDetailHref
  ) {
    const mediaLabel = metadata.type === "signature" ? "signature" : "photo";
    const mediaDescription =
      photos.length > 1 ? `${photos.length} task images` : `task ${mediaLabel}`;

    if (photos.length === 0 && !comment) return null;

    const tooltipContent = (
      <ResultTooltipContent
        photos={photos}
        comment={comment}
        taskDetailHref={taskDetailHref}
        mediaLabel={mediaLabel}
      />
    );

    return (
      <ResultTooltip
        describeChild
        placement="right-start"
        title={tooltipContent}
      >
        <div
          className={gridClasses.mediaCell}
          tabIndex={params.hasFocus ? 0 : -1}
          aria-label={`View ${mediaDescription}${comment ? " and comment" : ""}`}
        >
          {photos.length > 0 && (
            <svg
              viewBox="0 0 24 24"
              width="40"
              height="30"
              className={gridClasses.mediaCellIcon}
              aria-hidden="true"
            >
              <path
                fill="#303687"
                d="M20,5A2,2 0 0,1 22,7V17A2,2 0 0,1 20,19H4C2.89,19 2,18.1 2,17V7C2,5.89 2.89,5 4,5H20M5,16H19L14.5,10L11,14.5L8.5,11.5L5,16Z"
              />
            </svg>
          )}
        </div>
      </ResultTooltip>
    );
  }

  function renderResultCell(params) {
    const cellValue = params.value;
    const metadata = getCellMetadata(params.row, params.field);
    const comment = getCellComment(params.row, params.field);
    const photos = getCellPhotos(params.row, params.field);
    const taskDetailHref = getTaskDetailEventHref(
      workflowID,
      params.row.id,
      metadata.eventId
    );

    if (metadata.type === "photo" || metadata.type === "signature") {
      return renderMediaResultCell(
        params,
        metadata,
        comment,
        photos,
        taskDetailHref
      );
    }

    const normalisedCellValue =
      typeof cellValue === "string"
        ? cellValue.trim().toLowerCase()
        : cellValue;
    const [isChipValue, negativeItemIndex] = contains(
      negativeItems,
      normalisedCellValue,
      "value"
    );
    const negativeItem = negativeItems[negativeItemIndex];

    const cellContent = isChipValue ? (
      <div className={gridClasses.chipWrapper}>
        <div className={negativeItem.colour}></div>
        {String(negativeItem.value).charAt(0).toUpperCase() +
          String(negativeItem.value).slice(1)}
      </div>
    ) : (
      cellValue
    );

    if (!comment && photos.length === 0) {
      if (isChipValue) return cellContent;

      return (
        <OverflowTooltip
          value={cellValue}
          tabIndex={params.hasFocus ? 0 : -1}
        />
      );
    }

    const emptyCellAriaLabel = comment
      ? photos.length > 0
        ? "Answer with comment and photos"
        : "Commented answer"
      : "Answer with photos";

    return (
      <ResultTooltip
        describeChild
        placement="right-start"
        title={
          <ResultTooltipContent
            photos={photos}
            comment={comment}
            taskDetailHref={taskDetailHref}
          />
        }
      >
        <div
          className={gridClasses.commentTooltipTrigger}
          tabIndex={params.hasFocus ? 0 : -1}
          aria-label={
            cellValue === null || cellValue === undefined || cellValue === ""
              ? emptyCellAriaLabel
              : undefined
          }
        >
          {cellContent}
        </div>
      </ResultTooltip>
    );
  }

  function processRTKTask(task) {
    let jsonData = { [CELL_METADATA_FIELD]: {} };

    let obsoleted = false;

    if (task.obsoleted) {
      obsoleted = true;
    }

    task.events.sort((a, b) => {
      const timeA = a.processedAt.seconds;
      const timeB = b.processedAt.seconds;
      return timeA - timeB;
    });

    let eventKeys = Object.keys(task.events);

    eventKeys.forEach(processRTKComponent);

    function processRTKComponent(componentEvent, index) {
      let processedEvent = getComponentInfo(task.events[componentEvent]);

      if (processedEvent !== undefined) {
        if (processedEvent.type === "start") {
          jsonData["startUser"] = processedEvent.userName;
          jsonData["startedAt"] = processedEvent.value;
          currentStartTime = processedEvent.time;
          jsonData["status"] = "In Progress";
          return;
        }

        let taskDuration;

        if (processedEvent.type === "end") {
          taskDuration = Duration.fromMillis(
            processedEvent.time.getTime() - currentStartTime.getTime()
          );
          jsonData["Task Ended"] = processedEvent.value;
          jsonData["status"] = "Completed";
          jsonData["duration"] = taskDuration.toFormat("hh'h 'mm'm 'ss's'");
        }

        if (obsoleted) {
          jsonData["status"] = "Obsoleted";
        }

        if (processedEvent.rootId) {
          jsonData[processedEvent.rootId] = processedEvent.value;
          jsonData[CELL_METADATA_FIELD][processedEvent.rootId] = {
            type: processedEvent.type,
            eventId: processedEvent.id,
            ...(processedEvent.comment && { comment: processedEvent.comment }),
            ...(processedEvent.photos.length > 0 && {
              photos: processedEvent.photos,
            }),
          };
        }
      }
    }
    return jsonData;
  }

  function generateNegativeGrid(rows) {
    let negativeGrid = [];
    rows.forEach((row) => {
      let fields = Object.keys(row);

      fields.forEach((field) => {
        let cellValue = row[field];

        if (typeof cellValue !== "string") {
          return;
        }
        let headerName =
          columns[contains(columns, field, "field")[1]].headerName;
        let negativeGridContains = contains(
          negativeGrid,
          headerName,
          "question"
        );
        console.log("cellvalue = ", cellValue, headerName);
        if (headerName === "Focus") {
          return;
        }
        if (
          cellValue.trim().toLowerCase() === "no" &&
          !negativeGridContains[0]
        ) {
          negativeGrid.push({
            question: headerName,
            count: 1,
            id: headerName,
            // view:{ rowId: row.id, field, rowIndex: apiRef.current?.getRowIndexRelativeToVisibleRows(row.id), colIndex: apiRef.current?.getColumnIndex(field)}
          });
        } else if (cellValue.trim().toLowerCase() === "no") {
          negativeGrid[negativeGridContains[1]].count += 1;
        }
      });
    });
    console.log("negativeGridGenerated", negativeGrid);
    return negativeGrid;
  }

  function CustomToolbar() {
    return (
      <GridToolbarContainer sx={{ order: "1" }}>
        <GridToolbarColumnsButton
          sx={{ ".css-1rtnrqa": { color: "#808080" } }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <GridToolbarExport
          sx={{ ".css-k58djc": { color: "#808080" } }}
          printOptions={{
            hideFooter: true,
            hideToolbar: true,
          }}
          csvOptions={{
            fileName: `${workflow
              .toLowerCase()
              .trim()}-${dateRange[0].toLocaleString()}`,
          }}
          slotProps={{
            tooltip: { title: "Export data" },
          }}
        />
      </GridToolbarContainer>
    );
  }

  return (
    <>
      <Breadcrumbs
        breadcrumbs={[
          { name: "Launchpad", href: "/main/launchpad" },
          {
            name: originName,
            href: `/main/launchpad/${
              originFrom == "workflow-history" ? "task-history" : originFrom
            }`,
          },
          {
            name: "Task Results",
            href: "#",
            current: true,
          },
        ]}
      />
      <main className="flex flex-col align-middle">
        <section
          className={`bg-[${headerColour}] p-2 flex flex-col justify-start h-96 items-center`}
        >
          <h1 className="text-[#4c4c4c]flex items-center gap-2 mx-auto w-fit">
            Task Results
          </h1>

          <p className="text-[#4c4c4c] text-lg text-center w-[26rem] md:w-[40rem]">
            Workflow: {workflow}
          </p>
        </section>

        <div className="relative top-[-14rem] md:top-[-15rem] mx-6">
          <section
            className={`flex flex-col md:flex-row gap-4 shadow-lg justify-start mb-2 w-[unset] bg-white rounded-xl overflow-hidden p-4 px-6 border-l-[12px] border-l-[${buttonColour}] items-center`}
          >
            <DateRangePicker
              sx={{ width: "15rem", minWidth: 300 }}
              value={dateRange}
              onChange={setDateRange}
              label="Date Range"
              slotProps={{
                shortcuts: { items: presets },
              }}
            />
            <Button
              variant="contained"
              onClick={dateQuery}
              disabled={loading}
              sx={{ backgroundColor: buttonColour, borderRadius: 20 }}
            >
              Search
            </Button>
            {loading && (
              <CircularProgress size="30px" sx={{ color: buttonColour }} />
            )}
          </section>
        </div>

        <div
          className="relative top-[-14rem] md:top-[-15rem] mx-6 bg-white rounded-xl overflow-hidden p-3 py-6 shadow-lg grow"
          aria-busy={loading}
        >
          {!loading && queryError ? (
            <div
              role="alert"
              className="bg-white mx-3 flex flex-col justify-center items-center py-10"
            >
              <p className="mb-2 text-lg text-wrap text-center px-4">
                We couldn't load the task results.
              </p>
              <p className="text-center px-4">
                Please try searching again.
              </p>
            </div>
          ) : !loading && rows.length === 0 ? (
            <div className="bg-white mx-3 flex flex-col justify-center items-center">
              <img
                width="300px"
                src="../../images/no-results-light.png"
                alt="no results"
              />
              <p className="mb-4 text-lg text-wrap text-center px-4">
                There were no results during this time period! Try changing the
                date range.
              </p>
              <p className="mb-10 text-lg bg-slate-200 rounded-3xl p-3 px-8">
                {dateRange[0]?.toLocaleString()} to{" "}
                {dateRange[1]?.toLocaleString()}
              </p>
            </div>
          ) : (
            <DataGridPro
              loading={loading}
              aria-label={loading ? "Loading task results" : "Task results"}
              slotProps={{
                loadingOverlay: {
                  variant: "skeleton",
                  noRowsVariant: "skeleton",
                },
                toolbar: {
                  csvOptions: {
                    fileName: workflow
                      ? `${workflow.toLowerCase().trim()}-${dateRange[0].toLocaleString()}`
                      : "task-results",
                  },
                },
              }}
              showToolbar={!loading}
              rows={rows}
              columns={
                loading && columns.length === 0 ? LOADING_GRID_COLUMNS : columns
              }
              columnGroupingModel={
                loading && columns.length === 0 ? [] : columnGroupingModel
              }
              disableColumnMenu={loading}
              onFilterModelChange={(e) =>
                console.log("onFilterModelChanged", e)
              }
              initialState={{ pinnedColumns: { right: ["detailsUrl"] }, columns:{orderedFields:["Focus", "Task Reference"]} }}
              apiRef={apiRef}
              sx={loading ? { minHeight: 360 } : undefined}
            />
            // <></>
          )}
        </div>
        <div className="relative top-[-14rem] md:top-[-15rem] mt-[1rem] mx-6 bg-white rounded-xl overflow-hidden p-3 py-6 shadow-lg grow">
          <DataGridPro
            rows={generateNegativeGrid(rows)}
            columns={[
              {
                field: "question",
                headerName: "Question Answered",
                id: "jkl2dsd3",
                flex: 1,
              },
              {
                field: "count",
                headerName: "No. Negative Responses",
                id: "jksdsdl53",
                flex: 1,
                renderCell: (params) => {
                  return (
                    <div
                      class={`${gridClasses.chip} ${gridClasses.negativeChip} ${gridClasses.negativeReponseTableChip}`}
                    >
                      {params.value}
                    </div>
                  );
                },
              },
              // {
              // 	field: 'view', headerName: 'View Cell', id: 'jklaaaaf53', flex: 1,
              // 	renderCell: (params) => {
              // 		const handleScrollAndFocus = () => {
              // 			const rowId = params.value.rowId;
              // 			const field = params.value.field;

              // 			const rowIndex = apiRef.current.getRowIndexRelativeToVisibleRows(rowId);
              // 			let colIndex = apiRef.current.getColumnIndex(field);

              // 			// Fallback if column is hidden/pinned or index not found
              // 			if (colIndex == null || colIndex < 0) {
              // 			  const visibles = apiRef.current.getVisibleColumns();
              // 			  colIndex = visibles.findIndex(c => c.field === field);
              // 			}

              // 			if (rowIndex < 0 || colIndex < 0) return; // row/col not visible

              // 			// 1) Scroll so the cell is rendered
              // 			apiRef.current.scrollToIndexes({ rowIndex, colIndex });
              // 			apiRef.current.setCellFocus(rowId, field);
              // 		}
              // 		return (
              // 			<Button variant='contained' onClick={handleScrollAndFocus} sx={{ backgroundColor: buttonColour, borderRadius: 20 }}>
              // 				View Cell
              // 			</Button>
              // 		);
              // 	},
              // }
            ]}
            initialState={{
              sorting:{
                sortModel:[{field:"count", sort:"desc"}]
                
              }
            }}
			slotProps={{
				toolbar: {
				  csvOptions: {
					fileName: `${workflow?.toLowerCase()?.trim()}-${dateRange[0].toLocaleString()}-${dateRange[1].toLocaleString()}-negatives`,
				  },
				},
			  }}
            // slots={{
            // 	toolbar: () => {
            // 		return (
            // 			<GridToolbarContainer sx={{ order: "1" }}>
            // 				<Box sx={{ flexGrow: 1 }} />
            // 				<GridToolbarExport
            // 					sx={{ '.css-k58djc': { color: '#808080' } }}
            // 					printOptions={{
            // 						hideFooter: true,
            // 						hideToolbar: true,
            // 					}}
            // 					csvOptions={{ fileName: `${workflow?.toLowerCase()?.trim()}-${dateRange[0].toLocaleString()}-${dateRange[1].toLocaleString()}-negatives` }}
            // 					slotProps={{
            // 						tooltip: { title: "Export data" },
            // 					}}
            // 				/>
            // 			</GridToolbarContainer>
            // 		);
            // 	}
            // }}
            showToolbar
          />
        </div>
      </main>
    </>
  );
}
